import { useState } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography
} from '@mui/material';
import { useMqttStore } from '@stores/mqttStore';
import { useStationDevicesQuery } from '@api/bff/stationDevices';
import { ActionAutocomplete } from './ActionAutocomplete';
import {
  hasNominalToken,
  resolveNominalToken
} from '../../utils/resolveTemplateVars';
import type { CommandStep, TestPointRef } from '../../types/steps';

type Props = {
  step: CommandStep;
  testPointRef?: TestPointRef | null;
  onChange: (updates: Partial<CommandStep>) => void;
};

export const MqttCommandForm = ({ step, testPointRef, onChange }: Props) => {
  const [showRawOverride, setShowRawOverride] = useState(() => !!step.topic);
  const stationId = useMqttStore(s => s.stationId);
  const { robots, activeStationId } = useStationDevicesQuery();
  const robot = robots.find(r => r.stationId === activeStationId) ?? robots[0];

  const topicPlaceholder = robot
    ? `voltbot/${stationId || '{stationId}'}/${robot.deviceType}/${robot.mqttDeviceId}/{action}`
    : 'voltbot/{stationId}/{deviceType}/{deviceId}/{action}';

  const action = step.action ?? '';
  const showPreview = testPointRef && hasNominalToken(action);

  const handleActionChange = (newAction: string) => {
    onChange({ action: newAction });
  };

  const handleRawOverrideToggle = (checked: boolean) => {
    setShowRawOverride(checked);
    if (!checked) {
      onChange({ topic: undefined });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      <ActionAutocomplete value={action} onChange={handleActionChange} />
      {testPointRef && (
        <Typography variant="caption" color="text.secondary">
          Use <code>{`{{nominal}}`}</code> to insert the test point value at
          runtime (e.g. {testPointRef.nominal} {testPointRef.functionId})
        </Typography>
      )}
      {showPreview && (
        <Typography
          variant="caption"
          color="info.main"
          sx={{ fontFamily: 'monospace' }}
        >
          Resolves to: {resolveNominalToken(action, testPointRef)}
        </Typography>
      )}
      <TextField
        label="Description"
        size="small"
        fullWidth
        multiline
        minRows={2}
        value={step.description ?? ''}
        onChange={e => onChange({ description: e.target.value })}
        placeholder="Plain-English description of what this step does"
      />
      <TextField
        label="Timeout (ms)"
        size="small"
        type="number"
        value={step.timeoutMs ?? ''}
        onChange={e => onChange({ timeoutMs: Number(e.target.value) })}
        sx={{ maxWidth: 200 }}
      />
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={showRawOverride}
            onChange={e => handleRawOverrideToggle(e.target.checked)}
          />
        }
        label={
          <Typography variant="body2" color="text.secondary">
            Override topic
          </Typography>
        }
      />
      {showRawOverride && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pl: 2,
            borderLeft: 2,
            borderColor: 'warning.main'
          }}
        >
          <Typography variant="caption" color="warning.main">
            Send to a custom MQTT topic instead of the configured station
            device.
          </Typography>
          <TextField
            label="Topic"
            size="small"
            fullWidth
            value={step.topic ?? ''}
            onChange={e => onChange({ topic: e.target.value })}
            placeholder={topicPlaceholder}
          />
        </Box>
      )}
    </Box>
  );
};
